<template>
    <div :id="paymentMethodName">
        <empty-state
            v-if="paymentProcessing"
            class="overlay fullscreen"
            statusIcon="loading"
            title="Processing your payment"
            subtitle="Please do not refresh the page"
        ></empty-state>

        <div class="selectable-card collapsable-card"
             :class="{ 'active' : paymentMethodIsSelected(this.paymentMethodName) }">
            <div class="card-body">
                <div class="card-header"
                     @click="setPaymentMethod(paymentMethodName)"
                     v-scroll-to="{
                        el: '#'+paymentMethodName,
                        offset: -94,
                    }">
                    <p>
                        Pay now<span class="d-none d-sm-inline"> on debit / credit card</span>
                    </p>
                    <ul class="payment-types">
                        <li class="mb-0">
                            <img src="https://res.cloudinary.com/wefix/image/upload/v1564673828/booking/paypal_card_icon.svg" alt="PayPal" class="card-icon" />
                        </li>
                        <li class="mb-0">
                            <img src="https://res.cloudinary.com/wefix/image/upload/v1564673888/booking/visa_card_icon.svg" alt="Visa" class="card-icon" />
                        </li>
                        <li class="mb-0">
                            <img src="https://res.cloudinary.com/wefix/image/upload/v1564673915/booking/mastercard_card_icon.svg" alt="Mastercard" class="card-icon" />
                        </li>
                        <li class="mb-0">
                            <img src="https://res.cloudinary.com/wefix/image/upload/v1564673936/booking/amex_card_icon.svg" alt="American Express" class="card-icon" />
                        </li>
                        <li class="mb-0">
                            <img srcset="https://res.cloudinary.com/wefix/image/upload/v1564673982/booking/discover_card_icon.png 1x, https://res.cloudinary.com/wefix/image/upload/v1564673982/booking/discover_card_icon_2x.png 2x"
                                 src="https://res.cloudinary.com/wefix/image/upload/v1564673982/booking/discover_card_icon.png"
                                 alt="Discover"
                                 class="card-icon" />
                        </li>
                    </ul>
                </div>
                <div class="collapse" :class="{ 'show': paymentMethodIsSelected(paymentMethodName) }">
                    <div class="collapse-body">
                        <div class="collapse-content">
                            <div id="dropin-container-3d-secure"></div>
                            <!-- Total Cost -->
                            <div class="col-12">
                                <table class="price-table grand-total">
                                    <tr class="total-cost">
                                        <td>Total:</td>
                                        <td><span v-html="currencySymbol"></span>{{ cartTotal }}</td>
                                    </tr>
                                </table>
                            </div>
                            <button class="btn btn-block btn-lg btn-rounded continue-btn mb-3" :disabled="isLoading || !dropInInstance" @click="confirmBooking()">
                                <span v-if="isLoading"><i class="fa fa-spinner fa-pulse"></i></span>
								{{ paymentButtonText }}
                            </button>
                            <div class="text-center" v-if="isBookingInStore">
                                <p class="mt-4 mb-2 text-sm">
                                    By completing your order, you agree to our <router-link :to="{name: 'Terms'}" target="_blank">terms and conditions</router-link> and <router-link :to="{name: 'Privacy'}" target="_blank">privacy policy</router-link>.
                                </p>
                            </div>
                            <div class="text-center" v-if="carrier === 'ee'">
                                <p class="mt-4 mb-2 text-sm">
									Payment will be made to Likewize Repair. The transaction will appear on your statement as Likewize Repair.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    const dropIn = require('braintree-web-drop-in');

    export default {
        name: "DropIn",

        props: [
            "isSelected"
        ],

        data() {
            return {
                dropInInstance: null,
                isLoading: false,
                paymentMethodName: "braintree",
                paymentProcessing: false,
            }
        },

        mounted() {
            this.isLoading = false;
            this.initDropIn();
        },

        computed: {
            ...mapGetters([
                "carrier",
                "carrierDetails",
                "booking",
                "braintreeToken",
                "cartTotal",
                "isBookingInStore",
                "paymentMethodIsSelected",
                "currencySymbol"
            ]),

            paymentButtonText() {
                if(this.carrier === 'ee') {
                    return "Complete payment and confirm booking";
                }

                return "Complete booking";
            }
        },

        methods: {
            ...mapActions([
                "chargeBooking",
                "setPaymentMethod",
                "displayToast",
                "displayToastFromResponseObjectOfErrors",
                "retrieveBooking"
            ]),

            confirmBooking() {
                let self = this;

                if (this.isLoading) return;

                let threeDSecureData = {
                    amount: this.booking.cart.total_price,
                    email: this.booking.email,
                    billingAddress: {
                        givenName: this.booking.billing_first_name,
                        surname: this.booking.billing_last_name,
                        phoneNumber: this.booking.phone_number,
                        streetAddress: this.booking.billing_address.line_1,
                        extendedAddress: this.booking.billing_address.line_2,
                        city: this.booking.billing_address.town,
                        state: this.booking.billing_address.town,
                        postalCode: this.booking.billing_address.postcode,
                        countryCodeAlpha2: this.carrierDetails.countryCodeAlpha2
                    },
                    challengeRequested: true
                };

                this.isLoading = true;
                this.paymentProcessing = true;
                this.dropInInstance.requestPaymentMethod({threeDSecure: threeDSecureData}, function (err, payload) {
                    if (err) {
                        self.displayToast({text: "There was a problem confirming payment. Please try again or contact your bank.", type: "error"});
                        self.isLoading = false;
                        self.paymentProcessing = false;
                        return;
                    }

                    self.chargeBooking({
                        paidViaCredit: false,
                        paymentType: "braintree",
                        token: payload.nonce
                    }).then(() => {
                        self.isLoading = false;
                        self.paymentProcessing = false;

                        self.$router.push({name: 'bookingComplete'});
                    }).catch(errors => {
                        self.isLoading = false;
                        self.paymentProcessing = false;

                        if (errors.response.data.code === 'BK27') {
                            self.retrieveBooking().then(() => {
                                self.$router.push({name: 'checkout'});
                            });

                        } else if (errors.response.data.code === 'RBK15') {
                            self.$emit("iwOrderIsOow");

                        } else {
                            self.displayToastFromResponseObjectOfErrors(errors);
                            self.dropInInstance.clearSelectedPaymentMethod()
                        }
                    });
                });
            },

            initDropIn() {
                let self = this;

                const common = {
                    flow: 'checkout',
                    amount: this.cartTotal,
                    currency: this.carrierDetails.currency
                };

                dropIn.create({
                    authorization: this.braintreeToken,
                    selector: '#dropin-container-3d-secure',
                    threeDSecure: true,
                    paypal: {
                        flow: common.flow,
                        amount: common.amount,
                        currency: common.currency
                    },
                    // paypalCredit: {
                    //     flow: common.flow,
                    //     amount: common.amount,
                    //     currency: common.currency
                    // },
                    // @see https://developers.braintreepayments.com/guides/apple-pay/configuration/javascript/v3
                    // applePay: {
                    //     displayName: 'My Store',
                    //     paymentRequest: {
                    //         total: {
                    //             label: 'My Store',
                    //             amount: common.amount
                    //         },
                    //         // We recommend collecting billing address information, at minimum
                    //         // billing postal code, and passing that billing postal code with all
                    //         // Apple Pay transactions as a best practice.
                    //         requiredBillingContactFields: ["postalAddress"]
                    //     }
                    // },
                    // @see https://developers.braintreepayments.com/guides/google-pay/configuration/javascript/v3
                    // googlePay: {
                    //     googlePayVersion: 2,
                    //     merchantId: 'merchant-id-from-google',
                    //     transactionInfo: {
                    //         totalPriceStatus: 'FINAL',
                    //         totalPrice: common.amount,
                    //         currencyCode: common.currency
                    //     },
                    //     allowedPaymentMethods: [{
                    //         type: 'CARD',
                    //         parameters: {
                    //             // We recommend collecting and passing billing address information with all Google Pay transactions as a best practice.
                    //             billingAddressRequired: true,
                    //             billingAddressParameters: {
                    //                 format: 'FULL'
                    //             }
                    //         }
                    //     }]
                    // }
                }, function (err, dropInInstance) {
                    if (err) {
                        // Handle any errors that might've occurred when creating Drop-in
                        return;
                    }

                    self.dropInInstance = dropInInstance;
                });
            }
        }
    }
</script>

<style>
    [data-braintree-id="choose-a-way-to-pay"],
    [data-braintree-id="choose-a-way-to-pay"] + .braintree-placeholder,
    [data-braintree-id="card-view-icons"] {
        display: none
    }

    [data-braintree-id="payment-options-container"] > div {
        background-color: #ffffff;
        border: 1px solid #dbdbdb;
        border-radius: 4px!important;
        margin-bottom: 10px;
        font-size: 0.938rem;
    }

    .braintree-upper-container:before {
        background: #fff;
    }

    [data-braintree-id="payment-options-container"] .braintree-option__label {
        font-size: 0.938rem;
        margin-left: 10px;
    }

    [data-braintree-id="payment-options-container"] .braintree-option__logo {
        order: 2;
    }
</style>